import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import { userManagementGetById, userUpdateApi } from '../user.service';
import { getAllcountryManagement, listAdministrativeLocations } from '../../../countryManagement/countryManagement.service';
import Loader from '../../../../components/loader';
import AlertToast from '../../../../components/alertToast';
import FlagImage from '../../../../components/flagImage/FlagImage';
import { PHONE_MAX_LENGTH } from '../../../../constant/phone';
import { resultMessage } from '../../../../../service/apiResult';
import { editUserOnboardingSchema } from '../validationschema';
import { personaLabel as formatPersona } from '../../../../constant/personaLabel';
import {
  createMenuProps,
  FieldLabel,
  formFieldSx,
  helperTextSx,
  placeholderTextSx,
  sectionTitleSx,
} from '../../../../styles/formStyles';
import { primaryIconColor } from '../../../../styles/iconStyles';
import {
  createEditBackButtonSx,
  createEditCancelButtonSx,
  createEditCardSx,
  createEditFooterSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageSubtitleSx,
  createEditPageTitleSx,
  createEditPrimaryButtonSx,
} from '../../../../styles/pageFormStyles';

const MenuProps = createMenuProps();
const fieldSx = formFieldSx;
const ALERT_DURATION = 2000;

const asId = (value) => (value == null || value === '' ? '' : String(value));

/** `get` hands back the whole axios response; `attempt` then wraps that. */
const locationRows = (result) => {
  const rows = result?.data?.data ?? result?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

const withCurrent = (options, id, name) => {
  if (!id) return options;
  if (options.some((row) => String(row.id) === String(id))) return options;
  return [{ id, name: name || 'Current location', active: true }, ...options];
};

const activeOrSelected = (rows, selectedId) =>
  rows.filter((row) => row.active !== false || String(row.id) === String(selectedId));

function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [regions, setRegions] = useState([]);
  const [units, setUnits] = useState([]);
  const [lastMiles, setLastMiles] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState('');

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(editUserOnboardingSchema),
    defaultValues: {
      fullName: '',
      email: '',
      countryCode: '',
      phoneNumber: '',
      countryId: '',
      administrativeRegionId: '',
      administrativeUnitId: '',
      lastMileUnitId: '',
      pincode: '',
      persona: '',
    },
  });

  const selectedCountryId = watch('countryId');
  const selectedRegionId = watch('administrativeRegionId');
  const selectedUnitId = watch('administrativeUnitId');

  const detailsPath = `/userView/${id}`;

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const loadLocations = useCallback(async (countryId, parentId, selectedId, selectedName, setter) => {
    if (!countryId) {
      setter([]);
      return;
    }
    const result = await listAdministrativeLocations(countryId, parentId || null);
    const rows = activeOrSelected(locationRows(result), selectedId);
    setter(withCurrent(rows, selectedId, selectedName));
  }, []);

  const fetchUser = useCallback(async () => {
    setLoading(true);
    const response = await userManagementGetById(id);
    setLoading(false);
    const user = response?.data || {};
    setValue('fullName', user.fullName || '');
    setValue('email', user.email || '');
    setValue('countryCode', user.countryCode || '');
    setValue('phoneNumber', user.phoneNumber || '');
    setValue('countryId', asId(user.countryId));
    setValue('administrativeRegionId', asId(user.administrativeRegionId));
    setValue('administrativeUnitId', asId(user.administrativeUnitId));
    setValue('lastMileUnitId', asId(user.lastMileUnitId));
    setValue('pincode', user.pincode || '');
    setValue('persona', user.persona || '');

    if (user.countryId) {
      await loadLocations(
        user.countryId,
        null,
        user.administrativeRegionId,
        user.administrativeRegionName,
        setRegions
      );
      if (user.administrativeRegionId) {
        await loadLocations(
          user.countryId,
          user.administrativeRegionId,
          user.administrativeUnitId,
          user.administrativeUnitName,
          setUnits
        );
      }
      if (user.administrativeUnitId) {
        await loadLocations(
          user.countryId,
          user.administrativeUnitId,
          user.lastMileUnitId,
          user.lastMileUnitName,
          setLastMiles
        );
      }
    }
  }, [id, loadLocations, setValue]);

  const fetchCountries = useCallback(async () => {
    const res = await getAllcountryManagement(0, 100);
    const countries = Array.isArray(res?.data?.content) ? res.data.content : [];
    setCountryData(
      countries.filter((country) => country?.status === 'ACTIVE' || country?.active === true)
    );
  }, []);

  useEffect(() => {
    fetchCountries();
    fetchUser();
  }, [fetchCountries, fetchUser]);

  const onCountryChange = (countryId, fieldOnChange) => {
    fieldOnChange(countryId);
    setValue('administrativeRegionId', '');
    setValue('administrativeUnitId', '');
    setValue('lastMileUnitId', '');
    setUnits([]);
    setLastMiles([]);
    const selectedCountry = countryData.find((country) => String(country.id) === String(countryId));
    if (selectedCountry?.phoneCode) {
      setValue('countryCode', selectedCountry.phoneCode, { shouldValidate: true });
    }
    loadLocations(countryId, null, null, null, setRegions);
  };

  const onRegionChange = (regionId, fieldOnChange) => {
    fieldOnChange(regionId);
    setValue('administrativeUnitId', '');
    setValue('lastMileUnitId', '');
    setLastMiles([]);
    if (selectedCountryId && regionId) {
      loadLocations(selectedCountryId, regionId, null, null, setUnits);
    } else {
      setUnits([]);
    }
  };

  const onUnitChange = (unitId, fieldOnChange) => {
    fieldOnChange(unitId);
    setValue('lastMileUnitId', '');
    if (selectedCountryId && unitId) {
      loadLocations(selectedCountryId, unitId, null, null, setLastMiles);
    } else {
      setLastMiles([]);
    }
  };

  const onSubmit = async (data) => {
    const deepest =
      data.lastMileUnitId || data.administrativeUnitId || data.administrativeRegionId || null;
    const payload = {
      fullName: data.fullName.trim(),
      email: data.email.trim(),
      phoneNumber: data.phoneNumber || null,
      countryCode: data.countryCode || null,
      postalCode: data.pincode || null,
      administrativeLocationId: deepest || null,
    };

    try {
      setLoading(true);
      const response = await userUpdateApi(id, payload);
      setLoading(false);

      if (response?.success === false) {
        setResponseMsg(resultMessage(response, 'Failed to update user'));
        setAlert('false');
        setOpenAlert(true);
        return;
      }

      setResponseMsg('User updated successfully');
      setAlert('true');
      setOpenAlert(true);
      setTimeout(() => {
        navigate(detailsPath);
        alertClose();
      }, ALERT_DURATION);
    } catch (error) {
      setLoading(false);
      setResponseMsg('Something went wrong. Please try again.');
      setAlert('false');
      setOpenAlert(true);
    }
  };

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={() => navigate(detailsPath)}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>
        <Grid container alignItems="center">
          <Grid size={12}>
            <Typography className="app-page-title" sx={createEditPageTitleSx}>
              Edit User Onboarding
            </Typography>
            <Typography sx={createEditPageSubtitleSx}>
              Correct the applicant details shown on this user&apos;s record.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} className="material-form">
        <Box sx={createEditCardSx}>
          <Typography sx={sectionTitleSx}>Applicant Details</Typography>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Full Name" required />
              <FormControl fullWidth sx={helperTextSx}>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      id="fullName"
                      placeholder="Enter full name"
                      variant="outlined"
                      error={!!error}
                      helperText={error ? error.message : ''}
                      sx={{ ...fieldSx, ...helperTextSx }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Email" required />
              <FormControl fullWidth sx={helperTextSx}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      id="email"
                      placeholder="Enter email address"
                      variant="outlined"
                      error={!!error}
                      helperText={error ? error.message : ''}
                      sx={{ ...fieldSx, ...helperTextSx }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Phone Number" />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth error={!!errors.countryCode} sx={helperTextSx}>
                    <Controller
                      name="countryCode"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value || ''}
                          displayEmpty
                          MenuProps={MenuProps}
                          IconComponent={(iconProps) => (
                            <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />
                          )}
                          sx={fieldSx}
                          renderValue={(selected) => {
                            if (!selected) {
                              return <Box sx={placeholderTextSx}>Code</Box>;
                            }
                            const match = countryData.find((item) => item.phoneCode === selected);
                            return match ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FlagImage flagUrl={match.countryFlag} size={20} />
                                <span>{match.phoneCode}</span>
                              </Box>
                            ) : selected;
                          }}
                        >
                          {countryData.map((data) => (
                            <MenuItem key={`${data.id}-${data.phoneCode}`} value={data.phoneCode}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <FlagImage flagUrl={data.countryFlag} size={20} />
                                <span>{data.phoneCode}</span>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {!!errors.countryCode && <FormHelperText error>{errors.countryCode.message}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <FormControl fullWidth sx={helperTextSx}>
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          value={field.value || ''}
                          id="phoneNumber"
                          placeholder="Enter phone number"
                          onInput={(event) => {
                            event.target.value = event.target.value.replace(/[^0-9]/g, '');
                            if (event.target.value.length > PHONE_MAX_LENGTH) {
                              event.target.value = event.target.value.slice(0, PHONE_MAX_LENGTH);
                            }
                          }}
                          inputProps={{ maxLength: PHONE_MAX_LENGTH }}
                          variant="outlined"
                          error={!!error}
                          helperText={error ? error.message : ''}
                          sx={{ ...fieldSx, ...helperTextSx }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="User Persona" />
              <TextField
                value={formatPersona(watch('persona'))}
                disabled
                fullWidth
                variant="outlined"
                sx={fieldSx}
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Country" />
              <FormControl fullWidth error={!!errors.countryId} sx={helperTextSx}>
                <Controller
                  name="countryId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      displayEmpty
                      MenuProps={MenuProps}
                      IconComponent={(iconProps) => (
                        <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />
                      )}
                      sx={fieldSx}
                      onChange={(event) => onCountryChange(event.target.value, field.onChange)}
                      renderValue={(selected) =>
                        !selected ? (
                          <Box sx={placeholderTextSx}>Select country</Box>
                        ) : (
                          countryData.find((item) => String(item.id) === String(selected))?.name || selected
                        )
                      }
                    >
                      {countryData.map((data) => (
                        <MenuItem key={data.id} value={data.id}>
                          {data.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Administrative Region" />
              <FormControl fullWidth sx={helperTextSx}>
                <Controller
                  name="administrativeRegionId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      displayEmpty
                      disabled={!selectedCountryId}
                      MenuProps={MenuProps}
                      IconComponent={(iconProps) => (
                        <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />
                      )}
                      sx={fieldSx}
                      onChange={(event) => onRegionChange(event.target.value, field.onChange)}
                      renderValue={(selected) =>
                        !selected ? (
                          <Box sx={placeholderTextSx}>Select administrative region</Box>
                        ) : (
                          regions.find((item) => String(item.id) === String(selected))?.name || selected
                        )
                      }
                    >
                      {regions.map((data) => (
                        <MenuItem key={data.id} value={data.id}>
                          {data.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Administrative Unit Name" />
              <FormControl fullWidth sx={helperTextSx}>
                <Controller
                  name="administrativeUnitId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      displayEmpty
                      disabled={!selectedRegionId}
                      MenuProps={MenuProps}
                      IconComponent={(iconProps) => (
                        <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />
                      )}
                      sx={fieldSx}
                      onChange={(event) => onUnitChange(event.target.value, field.onChange)}
                      renderValue={(selected) =>
                        !selected ? (
                          <Box sx={placeholderTextSx}>Select administrative unit</Box>
                        ) : (
                          units.find((item) => String(item.id) === String(selected))?.name || selected
                        )
                      }
                    >
                      {units.map((data) => (
                        <MenuItem key={data.id} value={data.id}>
                          {data.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Last Mile Administrative Unit" />
              <FormControl fullWidth sx={helperTextSx}>
                <Controller
                  name="lastMileUnitId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      displayEmpty
                      disabled={!selectedUnitId}
                      MenuProps={MenuProps}
                      IconComponent={(iconProps) => (
                        <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />
                      )}
                      sx={fieldSx}
                      renderValue={(selected) =>
                        !selected ? (
                          <Box sx={placeholderTextSx}>Select last mile unit</Box>
                        ) : (
                          lastMiles.find((item) => String(item.id) === String(selected))?.name || selected
                        )
                      }
                    >
                      {lastMiles.map((data) => (
                        <MenuItem key={data.id} value={data.id}>
                          {data.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <FieldLabel label="Pincode" />
              <FormControl fullWidth sx={helperTextSx}>
                <Controller
                  name="pincode"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      id="pincode"
                      placeholder="Enter pincode"
                      variant="outlined"
                      error={!!error}
                      helperText={error ? error.message : ''}
                      sx={{ ...fieldSx, ...helperTextSx }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Box
            sx={{
              ...createEditFooterSx,
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
              <Button
                onClick={() => navigate(detailsPath)}
                sx={{ ...createEditCancelButtonSx, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, ml: { sm: 'auto' } }}>
              <Button variant="contained" type="submit" sx={{ ...createEditPrimaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                Update User
              </Button>
            </Box>
          </Box>
        </Box>
      </form>

      {loading && <Loader />}

      <Box textAlign="end">
        <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
        <AlertToast open={openAlert && alert === 'false'} onClose={alertClose} message={responseMsg} variant="error" />
      </Box>
    </Box>
  );
}

export default EditUser;
